import { type ApiContainerType, type ApiService, EnumContainerActionRequest } from '@Model';
import {
    Button,
    FieldWithError,
    FlexColumn,
    FloatingCard,
    type ILinkInputProperties,
    useTranslate,
    useWizardData,
    useWizardErrors,
    useWizardUpdate,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { type ContainerPosition, type ContainerStockLine, hasContent } from '../../containerOrder';
import { type RequisitionWizardState } from '../../RequisitionWizardState';
import { ContainerSummaryRow } from '../Summary/SummaryStepBody';
import { ContainerPositionForm } from './ContainerPositionForm';
import { contentError, type DetailsStepErrorKey } from './detailsErrors';
import { type ContainerStockView, StockStepBody } from './StockStepBody';

/** The fields of one container action of the exchange. */
export interface ExchangeSlotProps {
    position: ContainerPosition | undefined;
    containerTypeField: ILinkInputProperties<ApiContainerType>;
    contentField: ILinkInputProperties<ApiService>;
    /** The waste code of the chosen content, empty while none is known. */
    avvCode: string;
    onCount: (count: number) => void;
    onLocationHint: (locationHint: string) => void;
}

export interface ExchangeStepBodyProps {
    /** The container that goes. */
    pickup: ExchangeSlotProps & {
        onContainerNumber: (containerNumber: string) => void;
        onRemove: () => void;
    };
    /** The container that comes, undefined while no type has been named. */
    delivery: ExchangeSlotProps;
    stock: ContainerStockView;
    onTakeLine: (line: ContainerStockLine) => void;
}

/**
 * The exchange step: the container that goes, picked like a pickup, and the one that comes.
 *
 * One of the two is open for editing and the other stands beside it as a summary, so both actions
 * are in view all along. The server plans the container that comes as one of the same type as the one
 * that goes; the customer only checks it, and "Ändern" opens whichever container is wrong.
 */
// @ai-generated 2026-10-09T08:04:13+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ExchangeStepBody({ pickup, delivery, stock, onTakeLine }: ExchangeStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<DetailsStepErrorKey>();
    const { exchangeEditing } = useWizardData<RequisitionWizardState>();
    const update = useWizardUpdate<RequisitionWizardState>();

    const settledPickup = pickup.position && hasContent(pickup.position) ? pickup.position : undefined;
    const editing = settledPickup
        ? exchangeEditing ?? (settledPickup.origin === 'stock' ? undefined : 'pickup')
        : 'pickup';
    const deliveryError = delivery.position ? errors.content : errors.positions;

    const takeLine = (line: ContainerStockLine) => {
        onTakeLine(line);
        update({ exchangeEditing: undefined });
    };

    return (
        <FlexColumn align="stretch" gap="md">
            {editing === 'pickup' ? (
                <StockStepBody
                    {...pickup}
                    action={EnumContainerActionRequest.Exchange}
                    stock={stock}
                    onTakeLine={takeLine}
                />
            ) : (
                <>
                    <WizardStepTitle
                        title={i18n('Container tauschen')}
                        subtitle={i18n('Prüfe, welcher Container geht und welcher kommt.')}
                    />
                    <SettledContainerCard
                        title={i18n('Abholung')}
                        position={settledPickup}
                        onChange={() => update({ exchangeEditing: 'pickup' })}
                    />
                </>
            )}

            {editing === 'delivery' ? (
                <ContainerPositionForm
                    origin="placement"
                    title={i18n('Was kommt stattdessen?')}
                    subtitle={i18n('Wir haben den Container von oben übernommen — ändere, was anders sein soll.')}
                    position={delivery.position}
                    containerTypeField={delivery.containerTypeField}
                    contentField={delivery.contentField}
                    avvCode={delivery.avvCode}
                    contentError={contentError(errors, delivery.position)}
                    onCount={delivery.onCount}
                    location={{ label: i18n('Wo soll der Container stehen?'), onChange: delivery.onLocationHint }}
                />
            ) : (
                <FieldWithError error={settledPickup ? deliveryError : undefined}>
                    <SettledContainerCard
                        title={i18n('Ersatz')}
                        position={delivery.position}
                        onChange={() => update({ exchangeEditing: 'delivery' })}
                    />
                </FieldWithError>
            )}
        </FlexColumn>
    );
}

interface SettledContainerCardProps {
    title: string;
    /** Undefined while the action names no container yet, which the card says in place of a row. */
    position: ContainerPosition | undefined;
    onChange: () => void;
}

function SettledContainerCard({ title, position, onChange }: SettledContainerCardProps) {
    const i18n = useTranslate('');

    return (
        <FloatingCard
            title={title}
            subtitle={position ? undefined : i18n('Noch nicht festgelegt')}
            separateHeader={false}
            collapsible={false}
            additionalHeaderItems={
                <Button appearance="transparent" size="xs" onClick={onChange}>
                    {i18n('Ändern')}
                </Button>
            }
        >
            {position && <ContainerSummaryRow position={position} showsAction={false} i18n={i18n} />}
        </FloatingCard>
    );
}
